import React from 'react';
import { BarChart3, TrendingUp, PieChart as PieChartIcon, AreaChart as AreaChartIcon, ScatterChart as ScatterChartIcon, Donut } from 'lucide-react';
import type { ChartType } from '@shared/schemas/chart'; // For ChartType in CHART_TYPES_CONFIG
import type { ChartStudy, ChartColor } from '@shared/schemas/chart'; // For applyStudiesToData
import {
  ResponsiveContainer, ComposedChart, PieChart as RechartsPieChart, Pie, Cell, Bar, Area, Scatter, 
  XAxis, YAxis, CartesianGrid, Tooltip, Line
} from 'recharts';
import type { Chart as DBChart, ChartDataPoint } from '@shared/schemas/chart'; // Chart is DBChart
import type { ChartData } from '../store/chartAtoms'; // Local ChartData type
import { getColorPalette, type ColorPalette } from './colorPalettes';
import { getColorForCurrentTheme } from './themeAwareColors';
import tinycolor from 'tinycolor2';

export const CHART_COLORS = ['#ef4444', '#f97316', '#dc2626', '#ea580c', '#b91c1c'];
export const STUDY_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

// Helper function to convert ChartColor to string
export const getColorAsString = (color: ChartColor | undefined, isDarkMode?: boolean): string => {
  if (!color) return STUDY_COLORS[0]; // Default fallback
  if (typeof color === 'string') {
    return color;
  }
  // If it's a theme-aware color, get the appropriate color for current theme
  return getColorForCurrentTheme(color, isDarkMode || false);
};

// Function to get colors from selected palette
export const getChartColors = (paletteId?: string, customPalettes?: ColorPalette[]): string[] => {
  if (paletteId) {
    return getColorPalette(paletteId, customPalettes);
  }
  return CHART_COLORS;
};

// Renamed from CHART_TYPES to avoid conflict if imported into files that also have a local CHART_TYPES
// And made it more of a config object for direct use.
export const CHART_TYPE_CONFIG: Array<{
  id: ChartType;
  name: string;
  description: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
}> = [
  {
    id: 'bar',
    name: 'Bar Chart',
    description: 'Compare values across categories',
    icon: BarChart3
  },
  {
    id: 'line',
    name: 'Line Chart',
    description: 'Show trends over time',
    icon: TrendingUp
  },
  {
    id: 'area',
    name: 'Area Chart',
    description: 'Filled line chart showing volume',
    icon: AreaChartIcon
  },
  {
    id: 'pie',
    name: 'Pie Chart',
    description: 'Show proportions of a whole',
    icon: PieChartIcon
  },
  {
    id: 'doughnut',
    name: 'Doughnut Chart',
    description: 'Show proportions with a hole in the center',
    icon: Donut
  },
  {
    id: 'scatter',
    name: 'Scatter Plot',
    description: 'Show correlation between variables',
    icon: ScatterChartIcon
  }
];

export const getChartIconComponent = (chartType?: ChartType | string): React.FC<React.SVGProps<SVGSVGElement>> => {
  const config = CHART_TYPE_CONFIG.find(ct => ct.id === chartType);
  return config ? config.icon : BarChart3; // Default to BarChart3 icon
};

// --- Study Calculation Functions ---
// Note: These are defined as callable functions. If they were meant to be hooks themselves
// (e.g., for use inside React components directly with useCallback for memoization based on component lifecycle),
// they would need to be structured as custom hooks (useMyFunction) or be simple non-hook utility functions.
// For now, making them plain exportable functions. If useCallback was for optimizing their passing as props,
// the consuming component would wrap their call or reference in useCallback if needed.

export const calculateLinearRegression = (data: any[], xKey: string, yKey: string): any[] => {
  const n = data.length; if (n < 2) return data.map(p => ({...p, regression: null}));
  const sumX = data.reduce((sum, point) => sum + (point[xKey] || 0), 0);
  const sumY = data.reduce((sum, point) => sum + (point[yKey] || 0), 0);
  const sumXY = data.reduce((sum, point) => sum + (point[xKey] || 0) * (point[yKey] || 0), 0);
  const sumXX = data.reduce((sum, point) => sum + Math.pow(point[xKey] || 0, 2), 0);
  if ((n * sumXX - sumX * sumX) === 0) return data.map(p => ({...p, regression: p[yKey]}));
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  return data.map((point, index) => ({ ...point, regression: slope * index + intercept }));
};

export const calculateMovingAverage = (data: any[], yKey: string, period: number): any[] => {
  return data.map((point, index) => {
    if (index < period - 1) return { ...point, [`ma_${period}`]: null };
    const sum = data.slice(index - period + 1, index + 1).reduce((acc, p) => acc + (p[yKey] || 0), 0);
    return { ...point, [`ma_${period}`]: sum / period };
  });
};

export const solveLeastSquares = (matrix: number[][], results: number[]): number[] => {
  const n = matrix.length; 
  if (n === 0) return [];
  const m = matrix[0].length;
  if (m === 0) return [];
  const AtA: number[][] = Array(m).fill(0).map(() => Array(m).fill(0));
  const Atb: number[] = Array(m).fill(0);
  for (let i = 0; i < m; i++) for (let j = 0; j < m; j++) for (let k = 0; k < n; k++) AtA[i][j] += matrix[k][i] * matrix[k][j];
  for (let i = 0; i < m; i++) for (let k = 0; k < n; k++) Atb[i] += matrix[k][i] * results[k];
  const A_aug: number[][] = AtA.map((row, i) => [...row, Atb[i]]);
  for (let i = 0; i < m; i++) {
    let maxRow = i;
    for (let k = i + 1; k < m; k++) if (Math.abs(A_aug[k][i]) > Math.abs(A_aug[maxRow][i])) maxRow = k;
    [A_aug[i], A_aug[maxRow]] = [A_aug[maxRow], A_aug[i]];
    if (A_aug[i][i] === 0) continue; 
    for (let k = i + 1; k < m; k++) {
      const factor = A_aug[k][i] / A_aug[i][i];
      for (let j = i; j <= m; j++) A_aug[k][j] -= factor * A_aug[i][j];
    }
  }
  const x = Array(m).fill(0); 
  for (let i = m - 1; i >= 0; i--) {
    if (A_aug[i][i] === 0) { 
      for(let j=0; j<m; j++) x[j] = 0;
      return x; 
    }
    x[i] = A_aug[i][m] / A_aug[i][i];
    for (let k = i - 1; k >= 0; k--) A_aug[k][m] -= A_aug[k][i] * x[i];
  }
  return x;
};

export const calculatePolynomialTrend = (data: any[], _xKey: string, yKey: string, degree: number): any[] => {
  const n = data.length; if (n < degree + 1) return data.map(point => ({ ...point, [`poly_${degree}`]: null }));
  const matrix: number[][] = []; const results: number[] = [];
  for (let i = 0; i < n; i++) {
    const x = i; const y = data[i][yKey] || 0;
    const row: number[] = []; for (let j = 0; j <= degree; j++) row.push(Math.pow(x, j));
    matrix.push(row); results.push(y);
  }
  const coefficients = solveLeastSquares(matrix, results);
  if (coefficients.length === 0 || coefficients.length <= degree || coefficients.every(c => c === 0) ) { 
    return data.map(point => ({ ...point, [`poly_${degree}`]: null }));
  }
  return data.map((point, index) => {
    let polyVal = 0; for (let j = 0; j <= degree; j++) polyVal += (coefficients[j] || 0) * Math.pow(index, j);
    return { ...point, [`poly_${degree}`]: polyVal };
  });
};

export const calculateExponentialSmoothing = (data: any[], yKey: string, alpha: number): any[] => {
  if (data.length === 0) return [];
  const smoothedData = data.map(p => ({...p})); // Create a new array of new objects

  if (smoothedData.length > 0) {
    smoothedData[0][`exp_${alpha}`] = smoothedData[0][yKey] || 0;

    for (let i = 1; i < smoothedData.length; i++) {
      const prevSmoothedValue = smoothedData[i - 1][`exp_${alpha}`];
      // sPrev should always be a number after the first item is initialized.
      const sPrev = (typeof prevSmoothedValue === 'number' && !isNaN(prevSmoothedValue)) 
                    ? prevSmoothedValue 
                    : (smoothedData[i-1][yKey] || 0); // Fallback, though ideally not needed.
      const currentActualValue = smoothedData[i][yKey] || 0;
      smoothedData[i][`exp_${alpha}`] = alpha * currentActualValue + (1 - alpha) * sPrev;
    }
  }
  return smoothedData;
};

// --- NEW STATISTICAL FUNCTIONS ---
export const calculateMean = (dataYValues: number[]): number | null => {
  if (dataYValues.length === 0) return null;
  const sum = dataYValues.reduce((acc, val) => acc + (Number(val) || 0), 0);
  return sum / dataYValues.length;
};

export const calculateMedian = (dataYValues: number[]): number | null => {
  if (dataYValues.length === 0) return null;
  const sortedValues = [...dataYValues].sort((a, b) => a - b); // Ensure to sort a copy
  const mid = Math.floor(sortedValues.length / 2);
  return sortedValues.length % 2 !== 0 ? sortedValues[mid] : (sortedValues[mid - 1] + sortedValues[mid]) / 2;
};

export const calculateMode = (dataYValues: number[]): number[] | null => {
  if (dataYValues.length === 0) return null;
  const frequency: { [key: number]: number } = {};
  let maxFreq = 0;
  dataYValues.forEach(value => {
    const numValue = Number(value);
    if (isNaN(numValue)) return;
    frequency[numValue] = (frequency[numValue] || 0) + 1;
    if (frequency[numValue] > maxFreq) {
      maxFreq = frequency[numValue];
    }
  });
  if (maxFreq === 0) return null; 
  const allSameFrequency = Object.values(frequency).every(freq => freq === maxFreq);
  if (allSameFrequency && Object.keys(frequency).length > 1 && maxFreq === 1 && dataYValues.length > 1) return [];

  const modes: number[] = [];
  for (const valueKey in frequency) {
    if (frequency[valueKey] === maxFreq) {
      modes.push(Number(valueKey));
    }
  }
  // If all values are the same, it's a mode (e.g., [5,5,5] mode is 5)
  // If all values are different and occur once, no mode (e.g. [1,2,3]) - handled by allSameFrequency & maxFreq === 1
  // If dataset has one unique value e.g. [5,5,5] modes.length will be 1 and dataYValues.filter(v => v === modes[0]).length === dataYValues.length
  const uniqueValuesInDataset = new Set(dataYValues).size;
  if (modes.length === uniqueValuesInDataset && uniqueValuesInDataset > 1 && maxFreq === 1) return [];


  return modes;
};

export const calculateStdDeviation = (dataYValues: number[], mean: number | null): { stdDev: number | null, mean: number | null } => {
  if (dataYValues.length === 0 || mean === null) return { stdDev: null, mean: null };
  const variance = dataYValues.reduce((acc, val) => acc + Math.pow((Number(val) || 0) - mean, 2), 0) / dataYValues.length;
  return { stdDev: Math.sqrt(variance), mean };
};
// --- END NEW STATISTICAL FUNCTIONS ---

export const applyStudiesToData = (data: any[], studies: ChartStudy[], xKey: string, yKey: string): any[] => {
  let processedData = [...data];
  const dataYValues = processedData.map(p => Number(p[yKey])).filter(v => !isNaN(v));
  
  let currentMean: number | null = null; 

  studies.forEach(study => {
    if (!study.visible) return;
    switch (study.type) {
      case 'regression': processedData = calculateLinearRegression(processedData, xKey, yKey); break;
      case 'movingAverage': if (study.period) processedData = calculateMovingAverage(processedData, yKey, study.period); break;
      case 'polynomialTrend': if (study.degree) processedData = calculatePolynomialTrend(processedData, xKey, yKey, study.degree); break;
      case 'exponentialSmoothing': if (study.alpha !== undefined) processedData = calculateExponentialSmoothing(processedData, yKey, study.alpha); break;
      case 'mean':
        currentMean = calculateMean(dataYValues);
        if (currentMean !== null) {
          processedData = processedData.map(p => ({ ...p, meanValue: currentMean }));
        }
        break;
      case 'median':
        const median = calculateMedian(dataYValues);
        if (median !== null) {
          processedData = processedData.map(p => ({ ...p, medianValue: median }));
        }
        break;
      case 'mode':
        const modes = calculateMode(dataYValues);
        if (modes !== null && modes.length > 0) {
          processedData = processedData.map(p => ({ ...p, modeValue: modes[0] })); // Store first mode
        }
        break;
      case 'stdDeviation':
        if (currentMean === null) { 
            currentMean = calculateMean(dataYValues);
        }
        if (currentMean !== null) {
            const { stdDev } = calculateStdDeviation(dataYValues, currentMean);
            if (stdDev !== null) {
                processedData = processedData.map(p => ({ 
                    ...p, 
                    stdDevUpper: currentMean! + stdDev, 
                    stdDevLower: currentMean! - stdDev 
                }));
            }
        }
        break;
    }
  });
  return processedData;
};

export const renderSharedStudiesPreviewChart = (chartToPreview: ChartData | DBChart | any, studiesToApply: ChartStudy[]): JSX.Element | null => {
  if (!chartToPreview) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <p>No chart selected</p>
      </div>
    );
  }
  
  let data: any[] = []; 
  let chartTypeStr: string = 'bar';
  const isDb = !!(chartToPreview as DBChart).datasets; 

  if (isDb && (chartToPreview as DBChart).datasets) {
    const dataset = (chartToPreview as DBChart).datasets[0];
    if (!dataset || !dataset.data || dataset.data.length === 0) {
      return <div className="h-full flex items-center justify-center text-muted-foreground"><p>No data available for preview</p></div>;
    }
    data = dataset.data.map((point: ChartDataPoint, index: number) => ({ name: point.label, value: point.value, index: index }));
    chartTypeStr = (chartToPreview as DBChart).type || 'bar';
  } else if (!isDb && (chartToPreview as ChartData).data) { 
    const localChart = chartToPreview as ChartData;
    if (localChart.data.length === 0) {
      return <div className="h-full flex items-center justify-center text-muted-foreground"><p>No data available for preview</p></div>;
    }
    data = localChart.data.map((row: any, index: number) => ({ 
      name: localChart.xAxis ? String(row[localChart.xAxis]) : `P${index + 1}`,
      value: localChart.yAxis ? Number(row[localChart.yAxis]) : 0, 
      index: index 
    }));
    chartTypeStr = localChart.chartType || 'bar';
  } else { 
    return <div className="h-full flex items-center justify-center text-muted-foreground"><p>Chart data format not recognized for preview</p></div>;
  }

  if (data.length === 0) {
    return <div className="h-full flex items-center justify-center text-muted-foreground"><p>No data to display in preview</p></div>;
  }
  
  const processedData = applyStudiesToData(data, studiesToApply, 'index', 'value');
  const currentChartType = chartTypeStr as ChartType;

  if (currentChartType === 'pie' || currentChartType === 'doughnut') {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <RechartsPieChart>
          <Pie 
            data={processedData} cx="50%" cy="50%" 
            labelLine={false} 
            label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
            outerRadius={100} innerRadius={currentChartType === 'doughnut' ? 60 : 0} 
            fill="#8884d8" dataKey="value" nameKey="name"
          >
            {processedData.map((_entry: any, index: number) => (<Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />))}
          </Pie>
          <Tooltip />
        </RechartsPieChart>
      </ResponsiveContainer>
    );
  }
  const ChartComponent = ComposedChart;
  return (
    <ResponsiveContainer width="100%" height={400}>
      <ChartComponent data={processedData}>
        { chartToPreview?.previewDisplayOptions?.showGridLines !== false && <CartesianGrid strokeDasharray="3 3" /> }
        <XAxis dataKey="name" tick={{fontSize: 10}} />
        <YAxis tick={{fontSize: 11}} allowDecimals={false}/>
        <Tooltip />
        {currentChartType === 'bar' && <Bar dataKey="value" fill={CHART_COLORS[0]} name="Data" />}
        {currentChartType === 'area' && <Area type="monotone" dataKey="value" stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.6} name="Data" />}
        {currentChartType === 'scatter' && <Scatter dataKey="value" fill={CHART_COLORS[0]} name="Data" />}
        {currentChartType === 'line' && <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} name="Data" />}
        
        {studiesToApply.filter(s => s.type === 'regression' && s.visible).map(s => <Line key={s.id + '-reg'} type="monotone" dataKey="regression" stroke={getColorAsString(s.color, false)} strokeWidth={2} dot={false} strokeDasharray="5 5" name={s.name} />)}
        {studiesToApply.filter(s => s.type === 'polynomialTrend' && s.visible && s.degree).map(s => <Line key={s.id + '-poly'} type="monotone" dataKey={`poly_${s.degree}`} stroke={getColorAsString(s.color, false)} strokeWidth={2} dot={false} strokeDasharray="3 3" name={s.name} />)}
        {studiesToApply.filter(s => s.type === 'exponentialSmoothing' && s.visible && s.alpha !== undefined).map(s => <Line key={s.id + '-exp'} type="monotone" dataKey={`exp_${s.alpha}`} stroke={getColorAsString(s.color, false)} strokeWidth={2} dot={false} strokeDasharray="2 2" name={s.name} />)}
        {studiesToApply.filter(s => s.type === 'movingAverage' && s.visible && s.period).map(s => <Line key={s.id + '-ma'} type="monotone" dataKey={`ma_${s.period}`} stroke={getColorAsString(s.color, false)} strokeWidth={2} dot={false} name={s.name} />)}
      </ChartComponent>
    </ResponsiveContainer>
  );
};

// Function to generate a monochromatic palette (tints and shades)
export function generateMonochromaticPalette(baseColor: string, count: number = 9): string[] {
  const base = tinycolor(baseColor);
  if (!base.isValid()) {
    console.warn('[generateMonochromaticPalette] Invalid baseColor:', baseColor, 'defaulting to FireForge orange scale.');
    return ['#FFD0BF', '#FFAB8C', '#FF8659', '#FF6126', '#FF4500', '#E63E00', '#BF3400', '#992900', '#731F00'].slice(0, count);
  }

  const colors: string[] = [];
  const baseHsl = base.toHsl();

  const tintsToGenerate = Math.ceil((count - 1) / 2);
  const shadesToGenerate = Math.floor((count - 1) / 2);

  // Generate lighter shades (tints)
  for (let i = tintsToGenerate; i >= 1; i--) {
    const lightenAmount = i * (15 / (tintsToGenerate + 1)); // Adjust step for perceived lightness
    let currentHsl = { ...baseHsl };
    currentHsl.l = Math.min(1, baseHsl.l + lightenAmount / 100); 
    // Slightly desaturate very light tints if base is saturated
    if (baseHsl.s > 0.7 && currentHsl.l > 0.85) {
        currentHsl.s = Math.max(0, baseHsl.s - 0.15 * i);
    }
    colors.push(tinycolor(currentHsl).toHexString());
  }

  colors.push(base.toHexString()); // Add the base color itself

  // Generate darker shades
  for (let i = 1; i <= shadesToGenerate; i++) {
    const darkenAmount = i * (18 / (shadesToGenerate + 1)); // Adjust step for perceived darkness
    let currentHsl = { ...baseHsl };
    currentHsl.l = Math.max(0, baseHsl.l - darkenAmount / 100);
    // Slightly saturate very dark shades if base is not very saturated
    if (baseHsl.s < 0.6 && currentHsl.l < 0.3) {
        currentHsl.s = Math.min(1, baseHsl.s + 0.15 * i);
    }
    colors.push(tinycolor(currentHsl).toHexString());
  }
  
  // Ensure correct count by trimming (padding logic was too simple and might repeat)
  // If a more sophisticated padding is needed, it should involve generating intermediate HSL values.
  if (colors.length > count) {
    return colors.slice(0, count);
  } else if (colors.length < count) {
    // Fallback: if not enough colors, duplicate the last one (or first if only base was added)
    const lastColor = colors.length > 0 ? colors[colors.length - 1] : base.toHexString();
    while (colors.length < count) {
      colors.push(lastColor);
    }
  }
  return colors;
} 